import React, { useState, useEffect } from 'react';
import { Modal, GlassSelect } from './UI';
import { statisticsService, accountService } from '../apiServices';
import type { ExportMode, TransactionTypeFilter } from '../apiServices/statisticsService';
import type { Account } from '../apiServices/types';
import './ExportStatisticsModal.css';

interface ExportStatisticsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MODE_OPTIONS: { value: ExportMode; label: string }[] = [
  { value: 'MonthlyCategoryMatrix', label: 'Місячна матриця по категоріях' },
  { value: 'DetailedTransactions', label: 'Детальна витяжка транзакцій' },
];

const TRANSACTION_TYPE_OPTIONS: { value: TransactionTypeFilter; label: string }[] = [
  { value: 'Expenses', label: 'Витрати' },
  { value: 'Income', label: 'Доходи' },
  { value: 'Both', label: 'Витрати та доходи' },
];

const ExportStatisticsModal: React.FC<ExportStatisticsModalProps> = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState<ExportMode>('MonthlyCategoryMatrix');
  const [transactionType, setTransactionType] = useState<TransactionTypeFilter>('Both');
  const [includeSavings, setIncludeSavings] = useState(true);
  const [accountId, setAccountId] = useState<string>('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      accountService.getAll().then(setAccounts).catch(() => setAccounts([]));
      setError(null);
    }
  }, [isOpen]);

  const handleExport = async () => {
    setLoading(true);
    setError(null);
    try {
      await statisticsService.exportToCsv({
        mode,
        transactionType,
        includeSavings,
        accountId: accountId || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Помилка експорту');
    } finally {
      setLoading(false);
    }
  };

  const accountOptions = [
    { value: '', label: 'Всі рахунки' },
    ...accounts.map((a) => ({ value: a.id, label: a.name })),
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Експорт статистики в CSV"
      size="md"
      footer={
        <div className="export-modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Скасувати
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleExport}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="export-spinner" />
                Завантаження...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Завантажити CSV
              </>
            )}
          </button>
        </div>
      }
    >
      <div className="export-modal-body">
        {error && <div className="export-error">{error}</div>}

        <div className="form-group">
          <label className="form-label">Формат експорту</label>
          <GlassSelect
            options={MODE_OPTIONS}
            value={mode}
            onChange={(v) => setMode((v as ExportMode) ?? 'MonthlyCategoryMatrix')}
            placeholder="Оберіть формат"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Тип транзакцій</label>
          <GlassSelect
            options={TRANSACTION_TYPE_OPTIONS}
            value={transactionType}
            onChange={(v) => setTransactionType((v as TransactionTypeFilter) ?? 'Both')}
            placeholder="Оберіть тип"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Рахунок</label>
          <GlassSelect
            options={accountOptions}
            value={accountId}
            onChange={(v) => setAccountId(v ?? '')}
            placeholder="Всі рахунки"
          />
        </div>

        <div className="form-group export-checkbox">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={includeSavings}
              onChange={(e) => setIncludeSavings(e.target.checked)}
            />
            <span>Включити накопичення</span>
          </label>
        </div>
      </div>
    </Modal>
  );
};

export default ExportStatisticsModal;
